import { useEffect, useState } from "react";
import { Users, Copy, RefreshCw, Tablet, KeyRound, Trash2, Power, FileText } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { relTime } from "../lib/format";
import { STAFF_CODE_KEY } from "../pages/StaffLogin";

const copy = (t) => navigator.clipboard?.writeText(t).catch(() => {});

function PinShown({ who, pin, onClose }) {
  return (
    <div className="sf-pin" role="status" data-testid="staff-pin-shown">
      <p>PIN-kode til <b>{who}</b> – vises kun nu:</p>
      <div className="sf-pin-row">
        <code data-testid="staff-pin-value">{pin}</code>
        <button type="button" className="btn small" onClick={() => copy(pin)} data-testid="staff-pin-copy"><Copy size={14} /> Kopiér</button>
        <button type="button" className="btn small" onClick={onClose} data-testid="staff-pin-close">Færdig</button>
      </div>
    </div>
  );
}

export default function StaffSettings() {
  const { store, logout } = useAuth();
  const flash = useFlash();
  const [d, setD] = useState(null);
  const [v, setV] = useState({ name: "", username: "", pin: "" });
  const [errors, setErrors] = useState({});
  const [shown, setShown] = useState(null);
  const load = () => api.get("/staff").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d || store?.staff || store?.is_owner) return null;

  const act = async (fn, after) => {
    try { const r = await fn(); if (r.data.flash) flash(r.data.flash); after?.(r.data); }
    catch (e) { flash(apiError(e.response?.data?.detail) || "Fejl", "error"); }
    load();
  };
  const create = async (e) => {
    e.preventDefault(); setErrors({});
    try {
      const r = await api.post("/staff", { ...v, pin: v.pin || null });
      setShown({ who: r.data.item.name, pin: r.data.pin });
      setV({ name: "", username: "", pin: "" });
      load();
    } catch (ex) {
      if (ex.response?.data?.errors) setErrors(ex.response.data.errors);
      else flash(apiError(ex.response?.data?.detail) || "Fejl", "error");
    }
  };
  const prepDevice = async () => {
    if (!window.confirm("Gør denne enhed klar til personale? Du bliver logget ud, og enheden viser medarbejderlisten.")) return;
    const r = await api.post("/staff/device-token");
    localStorage.setItem(STAFF_CODE_KEY, r.data.code);
    logout();
    window.location.replace("/personale");
  };

  return (
    <section className="panel" id="medarbejdere" data-testid="staff-settings">
      <h2><Users size={18} /> Medarbejdere</h2>
      <p className="muted">Personale kan kun bruge Udlevering: udlevere, fortryde, markere "ikke hentet" og se kundens note. Ubegrænset antal – inkluderet i prisen.</p>

      <div className="sf-code" data-testid="staff-code-box">
        <div>
          <small>Butikskode</small>
          <code data-testid="staff-code">{d.code}</code>
        </div>
        <button type="button" className="btn small" onClick={() => copy(d.code)} data-testid="staff-code-copy"><Copy size={14} /> Kopiér</button>
        <button type="button" className="btn small" onClick={() => window.confirm("Lav en ny butikskode? Alle medarbejdere bliver logget ud.") && act(() => api.post("/staff/code/regenerate"))} data-testid="staff-code-regen">
          <RefreshCw size={14} /> Ny kode
        </button>
      </div>
      <details className="sf-howto" data-testid="staff-howto">
        <summary>Sådan logger personalet ind</summary>
        <ol>
          <li>Åbn <b>jatakpro.dk/personale</b> på tablet eller telefon.</li>
          <li>Skriv butikskoden <b>{d.code}</b>, dit brugernavn og din PIN.</li>
          <li>Næste gang husker enheden butikken – tryk bare på dit navn og skriv PIN.</li>
        </ol>
        <button type="button" className="btn small" onClick={() => window.print()} data-testid="staff-howto-print">Udskriv vejledning</button>
      </details>
      <p className="sf-guide">
        <a className="btn small" href="/guide/JaTak-Pro-medarbejderguide.pdf" target="_blank" rel="noopener" data-testid="staff-guide-pdf">
          <FileText size={14} /> Medarbejderguide (PDF)
        </a>{" "}
        <span className="muted">– del med dit personale</span>
      </p>
      <button type="button" className="btn" onClick={prepDevice} data-testid="staff-prep-device"><Tablet size={16} /> Gør denne enhed klar til personale</button>

      {shown && <PinShown {...shown} onClose={() => setShown(null)} />}

      <ul className="sf-list" data-testid="staff-list">
        {d.items.map((s) => (
          <li key={s.id} className={s.active ? "" : "off"} data-testid={`staff-row-${s.id}`}>
            <div className="sf-who">
              <b>{s.name}</b>
              <small>@{s.username} · {s.last_login_at ? `sidst logget ind ${relTime(s.last_login_at)}` : "aldrig logget ind"}
                {!s.active && " · deaktiveret"}{s.locked && " · låst i 15 min."}</small>
            </div>
            <div className="sf-btns">
              <button type="button" className="btn small" onClick={() => act(() => api.post(`/staff/${s.id}/pin`, {}), (r) => setShown({ who: s.name, pin: r.pin }))} data-testid={`staff-reset-pin-${s.id}`}><KeyRound size={14} /> Ny PIN</button>
              <button type="button" className="btn small" onClick={() => act(() => api.post(`/staff/${s.id}/active`, { active: !s.active }))} data-testid={`staff-toggle-${s.id}`}><Power size={14} /> {s.active ? "Deaktivér" : "Aktivér"}</button>
              <button type="button" className="btn small danger" onClick={() => window.confirm(`Slet ${s.name}?`) && act(() => api.delete(`/staff/${s.id}`))} data-testid={`staff-delete-${s.id}`}><Trash2 size={14} /></button>
            </div>
          </li>
        ))}
        {!d.items.length && <li className="muted" data-testid="staff-empty">Ingen medarbejdere endnu.</li>}
      </ul>

      <form className="sf-add" onSubmit={create} noValidate data-testid="staff-add-form">
        <h3>Tilføj medarbejder</h3>
        <div className="tc-grid">
          {[["name", "Navn", "Fx Line"], ["username", "Brugernavn", "Fx line"], ["pin", "PIN (4 cifre)", "Tom = tilfældig PIN"]].map(([k, l, ph]) => (
            <div className="field" key={k}>
              <label htmlFor={`sf-${k}`}>{l}</label>
              <input id={`sf-${k}`} value={v[k]} placeholder={ph} inputMode={k === "pin" ? "numeric" : undefined} maxLength={k === "pin" ? 4 : 40}
                autoComplete="off" onChange={(e) => setV({ ...v, [k]: k === "pin" ? e.target.value.replace(/\D/g, "") : e.target.value })} data-testid={`staff-add-${k}`} />
              {errors[k] && <div className="err" data-testid={`staff-add-${k}-error`}>{errors[k]}</div>}
            </div>
          ))}
        </div>
        <div className="sf-btns">
          <button className="btn primary" data-testid="staff-add-submit">Opret medarbejder</button>
          <button type="button" className="btn" onClick={() => setV({ ...v, pin: String(Math.floor(1000 + Math.random() * 9000)) })} data-testid="staff-add-random">Lav en tilfældig PIN</button>
        </div>
      </form>
    </section>
  );
}
